import React, { useState, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import MainLayout from "../../../components/layout/MainLayout";
import { getAllWorkTypes } from "../../../../Services/workTypeService";
import { getAllCompensationTypes } from "../../../../Services/compensationService";
import { toast } from "react-toastify";
import { getSubscriptionData } from "../../../../Services/subscriptionService";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { getProjectByIdService } from "../../../../Services/projectService";
import { createTimeEntry } from "../../../../Services/timeEntryService";
import { getAllUserByCreatedBy } from "../../../../Services/authService";
import { getApprovalFormByProjectId } from "../../../../Services/approvalFormService";

export default function TimeEntryForm() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const userId = useSelector((state) => state.auth.loggedInUser._id);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedScaffold, setSelectedScaffold] = useState(null);
  const [date, setDate] = useState(formatDate(new Date()));
  const [hours, setHours] = useState("");
  const [selectedWorkType, setSelectedWorkType] = useState(null);
  const [selectedCompensation, setSelectedCompensation] = useState(null);
  const [comments, setComments] = useState("");
  const [approvalStatus, setApprovalStatus] = useState(null);

  const [projectDetails, setProjectDetails] = useState({});
  const [workTypes, setWorkTypes] = useState([]);
  const [compensationTypes, setCompensationTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [approvalStatuses, setApprovalStatuses] = useState([]);
  const [error, setError] = useState("");

  const [loading, setLoading] = useState({
    users: false,
    project: false,
    scaffolds: false,
    workTypes: false,
    compensationTypes: false,
    saveEntry: false,
    approvalStatuses: false,
  });

  // Dropdown states
  const [usersDropdownOpen, setUsersDropdownOpen] = useState(false);
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [scaffoldDropdownOpen, setScaffoldDropdownOpen] = useState(false);
  const [workTypeDropdownOpen, setWorkTypeDropdownOpen] = useState(false);
  const [compensationDropdownOpen, setCompensationDropdownOpen] =
    useState(false);
  const [approvalDropdownOpen, setApprovalDropdownOpen] = useState(false);

  const [availableScaffolds, setAvailableScaffolds] = useState([]);
  function formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  // Toggle user selection
  function toggleUserSelection(user) {
    if (selectedUsers.some((u) => u._id === user._id)) {
      setSelectedUsers(selectedUsers.filter((u) => u._id !== user._id));
    } else {
      setSelectedUsers([...selectedUsers, user]);
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedUsers.length) {
      toast.error("Please select at least one user");
      return;
    }

    try {
      setLoading((prev) => ({ ...prev, saveEntry: true }));
      const successEntries = [];
      const errors = [];
      await Promise.all(
        selectedUsers?.map(async (user) => {
          // Create base object with only the user ID
          const entryData = {
            userId: user?._id,
          };

          // Only add fields that have valid values
          if (selectedProject?._id) entryData.projectId = selectedProject._id;
          if (selectedScaffold?._id)
            entryData.scaffoldId = selectedScaffold._id;
          if (date) entryData.date = date;
          if (hours) entryData.totalHours = parseFloat(hours); // Note: changed to totalHours to match backend
          if (selectedWorkType?._id)
            entryData.workTypeId = selectedWorkType._id;
          if (selectedCompensation?._id)
            entryData.compensationTypeId = selectedCompensation._id;
          if (comments?.trim()) entryData.comments = comments;

          entryData.approvalStatus = approvalStatus?.value || "pending";

          try {
            const response = await createTimeEntry(entryData);
            if (response?.success) {
              successEntries.push(response.data);
              toast.success(`Entry created for ${user?.name || "user"}`);
              navigate("/admin/time-track-list");
            } else {
              const errorMessage = `Failed for ${user?.name || "user"}: ${
                response?.message || "Unknown error"
              }`;
              errors.push(errorMessage);
              toast.error(errorMessage);
            }
          } catch (error) {
            const errorMessage = `Failed for ${user?.name || "user"}: ${
              error?.message || "Unknown error"
            }`;
            errors.push(errorMessage);
            toast.error(errorMessage);
          }
        })
      );
    } catch (error) {
      console.error("Error creating time entries:", error);
      toast.error("Failed to create entries");
    } finally {
      setLoading((prev) => ({ ...prev, saveEntry: false }));
    }
  };

  const getAllWorkTypesData = async () => {
    try {
      setLoading((prev) => ({ ...prev, workTypes: true }));
      const responseData = await getAllWorkTypes();
      if (responseData?.data) {
        setWorkTypes(responseData.data);

        // Look for 'Regular' work type first, then fall back to first item
        const regularWorkType = responseData.data.find(
          (workType) =>
            workType.name?.toLowerCase() === "regular" ||
            workType.workTypeName?.toLowerCase() === "regular"
        );

        if (regularWorkType) {
          setSelectedWorkType(regularWorkType);
        } else if (responseData.data.length > 0) {
          // Fallback to first work type if 'Regular' is not found
          setSelectedWorkType(responseData.data[0]);
        }
      }
    } catch (error) {
      console.error("Error fetching work types:", error);
      toast.error(error.response?.data?.message || "Failed to load work types");
      setError("Failed to load work types. Please try again later.");
    } finally {
      setLoading((prev) => ({ ...prev, workTypes: false }));
    }
  };

  const getAllCompensationTypesData = async () => {
    try {
      setLoading((prev) => ({ ...prev, compensationTypes: true }));
      const responseData = await getAllCompensationTypes();
      if (responseData?.data) {
        setCompensationTypes(responseData.data);
        // Set default compensation to "None" or first option
        const noneCompensation = responseData.data.find(
          (comp) => comp.name.toLowerCase() === "none"
        );
        setSelectedCompensation(noneCompensation || responseData.data[0]);
      }
    } catch (error) {
      console.error("Error fetching compensation types:", error);
      toast.error(
        error.response?.data?.message || "Failed to load compensation types"
      );
      setError("Failed to load compensation types. Please try again later.");
    } finally {
      setLoading((prev) => ({ ...prev, compensationTypes: false }));
    }
  };

  const getUserDetails = async () => {
    try {
      setLoading((prev) => ({ ...prev, users: true }));
      const dataResponse = await getAllUserByCreatedBy(userId);
      const userDataResponse = dataResponse?.data;
      const filteredUsers = userDataResponse;

      setUsers(filteredUsers);
    } catch (error) {
      console.error("Error fetching subscription data:", error);
      toast.error(error.response?.data?.message || "Failed to load user data");
      setError("Failed to load user data. Please try again later.");
    } finally {
      setLoading((prev) => ({ ...prev, users: false }));
    }
  };

  const getProjectDetail = async () => {
    try {
      setLoading((prev) => ({ ...prev, project: true }));
      const projectResponse = await getProjectByIdService(projectId);
      const projectDetails = projectResponse?.data?.project;

      if (projectDetails) {
        setProjectDetails(projectDetails);
        setSelectedProject(projectDetails);
        const approvalDetails = await getApprovalFormByProjectId(projectId);

        if (approvalDetails?.data) {
          const approvalData = approvalDetails?.data;
          setAvailableScaffolds(approvalData);
        }
      }
    } catch (error) {
      console.error("Error fetching project details:", error);
      toast.error(
        error.response?.data?.message || "Failed to load project details"
      );
      setError("Failed to load project details. Please try again later.");
    } finally {
      setLoading((prev) => ({ ...prev, project: false }));
    }
  };

  const getApprovalStatuses = async () => {
    try {
      setLoading((prev) => ({ ...prev, approvalStatuses: true }));
      // Replace with actual API call when available
      // For now using hardcoded values
      const statuses = [
        { id: 1, name: "Pending", value: "pending" },
        { id: 2, name: "Approved", value: "approved" },
        { id: 3, name: "Rejected", value: "rejected" },
      ];
      setApprovalStatuses(statuses);
      setApprovalStatus(statuses[0]);
    } catch (error) {
      console.error("Error fetching approval statuses:", error);
      setError("Failed to load approval statuses. Please try again later.");
    } finally {
      setLoading((prev) => ({ ...prev, approvalStatuses: false }));
    }
  };

  useEffect(() => {
    getProjectDetail();
    getUserDetails();
    getAllWorkTypesData();
    getAllCompensationTypesData();
    getApprovalStatuses();
  }, []);

  // Reset form handler
  const handleReset = () => {
    setSelectedUsers([]);
    setSelectedScaffold(null);
    setDate(formatDate(new Date()));
    setHours("");

    // Set work type to 'Regular' by default, fallback to first if not found
    const regularWorkType = workTypes.find(
      (workType) =>
        workType.name?.toLowerCase() === "regular" ||
        workType.workTypeName?.toLowerCase() === "regular"
    );
    setSelectedWorkType(
      regularWorkType || (workTypes.length > 0 ? workTypes[0] : null)
    );

    setSelectedCompensation(
      compensationTypes.find((comp) => comp.name.toLowerCase() === "none") ||
        (compensationTypes.length > 0 ? compensationTypes[0] : null)
    );
    setComments("");
    setApprovalStatus(approvalStatuses.length > 0 ? approvalStatuses[0] : null);
  };

  return (
    <MainLayout title={"Add Time Entry"}>
      <div className="mx-auto bg-white p-6">
        <h1 className="text-2xl font-medium text-gray-800 border-b pb-4 mb-6">
          Add New Time Entry
        </h1>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* User Field */}
            <div>
              <label className="block mb-1 text-sm font-medium">
                User <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <button
                  type="button"
                  className="w-full flex items-center justify-between border rounded px-3 py-2 text-left"
                  onClick={() => setUsersDropdownOpen(!usersDropdownOpen)}
                  disabled={loading.users}
                >
                  {loading.users ? (
                    <span>Loading users...</span>
                  ) : selectedUsers.length > 0 ? (
                    `${selectedUsers.length} user${
                      selectedUsers.length > 1 ? "s" : ""
                    } selected`
                  ) : (
                    "Select User"
                  )}
                  <ChevronDown className="h-4 w-4" />
                </button>

                {usersDropdownOpen && !loading.users && (
                  <div className="absolute z-10 mt-1 w-full bg-white border rounded shadow-lg">
                    <ul className="py-1 max-h-60 overflow-auto">
                      {users?.length > 0 ? (
                        users?.map((user) => (
                          <li
                            key={user._id}
                            className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center"
                            onClick={() => toggleUserSelection(user)}
                          >
                            <input
                              type="checkbox"
                              checked={selectedUsers.some(
                                (u) => u._id === user._id
                              )}
                              onChange={() => {}}
                              className="mr-2"
                            />
                            {user.name || user.userName || user.userEmail}
                          </li>
                        ))
                      ) : (
                        <li className="px-3 py-2 text-gray-500">
                          No users available
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                You can select multiple users to create entries for all of them
                at once
              </p>
            </div>

            {/* Project Field */}
            <div>
              <label className="block mb-1 text-sm font-medium">
                Project <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <button
                  type="button"
                  className="w-full flex items-center justify-between border rounded px-3 py-2 text-left"
                  disabled={loading.project || projectId}
                >
                  {loading.project ? (
                    <span>Loading project...</span>
                  ) : selectedProject ? (
                    selectedProject.name ||
                    selectedProject.projectName ||
                    "Selected Project"
                  ) : (
                    "Select Project"
                  )}
                  <ChevronDown
                    className={`h-4 w-4 ${projectId ? "opacity-50" : ""}`}
                  />
                </button>
              </div>
              {projectId && (
                <p className="text-xs text-gray-500 mt-1">
                  Project is pre-selected based on URL parameter
                </p>
              )}
            </div>

            {/* Scaffold ID Field */}
            <div>
              <label className="block mb-1 text-sm font-medium">
                Scaffold ID
              </label>
              <div className="relative">
                <button
                  type="button"
                  className="w-full flex items-center justify-between border rounded px-3 py-2 text-left"
                  onClick={() => setScaffoldDropdownOpen(!scaffoldDropdownOpen)}
                  disabled={!selectedProject || loading.scaffolds}
                >
                  {loading.scaffolds ? (
                    <span>Loading scaffolds...</span>
                  ) : selectedScaffold ? (
                    selectedScaffold?.scaffoldId || "Selected Scaffold"
                  ) : (
                    "Select Scaffold ID"
                  )}
                  <ChevronDown className="h-4 w-4" />
                </button>
                {scaffoldDropdownOpen && availableScaffolds?.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-white border rounded shadow-lg">
                    <ul className="py-1 max-h-60 overflow-auto">
                      {availableScaffolds?.map((scaffold) => (
                        <li
                          key={scaffold._id}
                          className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                          onClick={() => {
                            setSelectedScaffold(scaffold);
                            setScaffoldDropdownOpen(false);
                          }}
                        >
                          {scaffold?.scaffoldId ||
                            scaffold?.scaffolderowner ||
                            scaffold?.scaffoldIdentificationNumber}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {/* Date Field */}
            <div>
              <label className="block mb-1 text-sm font-medium">
                Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full border rounded px-3 py-2"
                required
              />
            </div>

            {/* Hours Field */}
            <div>
              <label className="block mb-1 text-sm font-medium">Hours</label>
              <input
                type="number"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                className="w-full border rounded px-3 py-2"
                step="0.25"
                min="0"
                required
              />
            </div>

            {/* Work Type Field */}
            <div>
              <label className="block mb-1 text-sm font-medium">
                Work Type <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <button
                  type="button"
                  className="w-full flex items-center justify-between border rounded px-3 py-2 text-left"
                  onClick={() => setWorkTypeDropdownOpen(!workTypeDropdownOpen)}
                  disabled={loading.workTypes}
                >
                  {loading.workTypes ? (
                    <span>Loading work types...</span>
                  ) : selectedWorkType ? (
                    selectedWorkType.name || selectedWorkType.workTypeName
                  ) : (
                    "Select Work Type"
                  )}
                  <ChevronDown className="h-4 w-4" />
                </button>

                {workTypeDropdownOpen && !loading.workTypes && (
                  <div className="absolute z-10 mt-1 w-full bg-white border rounded shadow-lg">
                    <ul className="py-1 max-h-60 overflow-auto">
                      {workTypes.length > 0 ? (
                        workTypes?.map((workType) => (
                          <li
                            key={workType._id}
                            className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              setSelectedWorkType(workType);
                              setWorkTypeDropdownOpen(false);
                            }}
                          >
                            {workType.name || workType.workTypeName}
                          </li>
                        ))
                      ) : (
                        <li className="px-3 py-2 text-gray-500">
                          No work types available
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {/* Additional Compensation Field */}
            <div>
              <label className="block mb-1 text-sm font-medium">
                Additional Compensation
              </label>
              <div className="relative">
                <button
                  type="button"
                  className="w-full flex items-center justify-between border rounded px-3 py-2 text-left"
                  onClick={() =>
                    setCompensationDropdownOpen(!compensationDropdownOpen)
                  }
                  disabled={loading.compensationTypes}
                >
                  {loading.compensationTypes ? (
                    <span>Loading compensation types...</span>
                  ) : selectedCompensation ? (
                    selectedCompensation.name ||
                    selectedCompensation.compensationTypeName
                  ) : (
                    "Select Compensation"
                  )}
                  <ChevronDown className="h-4 w-4" />
                </button>

                {compensationDropdownOpen && !loading.compensationTypes && (
                  <div className="absolute z-10 mt-1 w-full bg-white border rounded shadow-lg">
                    <ul className="py-1 max-h-60 overflow-auto">
                      {compensationTypes?.length > 0 ? (
                        compensationTypes?.map((comp) => (
                          <li
                            key={comp._id}
                            className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              setSelectedCompensation(comp);
                              setCompensationDropdownOpen(false);
                            }}
                          >
                            {comp.name || comp.compensationTypeName}
                          </li>
                        ))
                      ) : (
                        <li className="px-3 py-2 text-gray-500">
                          No compensation types available
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {/* Comments Field - Full Width */}
            <div className="md:col-span-2">
              <label className="block mb-1 text-sm font-medium">Comments</label>
              <textarea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                className="w-full border rounded px-3 py-2 h-32"
                placeholder="Enter any additional comments here..."
              />
            </div>

            {/* Approval Status Field */}
            <div>
              <label className="block mb-1 text-sm font-medium">
                Approval Status
              </label>
              <div className="relative">
                <button
                  type="button"
                  className="w-full flex items-center justify-between border rounded px-3 py-2 text-left"
                  onClick={() => setApprovalDropdownOpen(!approvalDropdownOpen)}
                  disabled={loading.approvalStatuses}
                >
                  {loading.approvalStatuses ? (
                    <span>Loading approval statuses...</span>
                  ) : approvalStatus ? (
                    approvalStatus.name
                  ) : (
                    "Select Status"
                  )}
                  <ChevronDown className="h-4 w-4" />
                </button>

                {approvalDropdownOpen && !loading.approvalStatuses && (
                  <div className="absolute z-10 mt-1 w-full bg-white border rounded shadow-lg">
                    <ul className="py-1 max-h-60 overflow-auto">
                      {approvalStatuses.length > 0 ? (
                        approvalStatuses?.map((status) => (
                          <li
                            key={status.id}
                            className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              setApprovalStatus(status);
                              setApprovalDropdownOpen(false);
                            }}
                          >
                            {status.name}
                          </li>
                        ))
                      ) : (
                        <li className="px-3 py-2 text-gray-500">
                          No approval statuses available
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Buttons - Right Aligned */}
          <div className="flex justify-end gap-3 mt-8">
            <button
              type="button"
              className="px-4 py-2 border rounded text-gray-700 hover:bg-gray-50"
              onClick={handleReset}
              disabled={loading.saveEntry}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-300"
              disabled={loading.saveEntry}
            >
              {loading.saveEntry ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </MainLayout>
  );
}
